import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

interface Device {
  id: string
  device_id: string
  name: string
  type: 'scanner' | 'microscope' | 'processor' | 'workstation' | 'server' | 'other'
  manufacturer: string
  model: string
  serial_number: string
  location: string
  department: string
  status: 'online' | 'offline' | 'maintenance' | 'error' | 'warning'
  last_seen: string
  uptime_percentage: number
  current_load: number
  temperature?: number
  firmware_version: string
  ip_address: string
  installed_date: string
  warranty_expiry: string
  maintenance_due?: string
}

interface DeviceAlert {
  id: string
  device_id: string
  device_name: string
  alert_type: 'error' | 'warning' | 'info'
  message: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  created_at: string
  acknowledged: boolean
}

const DeviceStatus: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([])
  const [alerts, setAlerts] = useState<DeviceAlert[]>([])
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [locationFilter, setLocationFilter] = useState<string>('all')
  const [showDeviceModal, setShowDeviceModal] = useState(false)

  const fetchDevices = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/v1/devices/status/')
      if (response.ok) {
        const data = await response.json()
        const deviceList = Array.isArray(data) ? data : (data.results || [])
        setDevices(Array.isArray(deviceList) ? deviceList : [])
      } else {
        toast.error('Failed to fetch devices')
      }
    } catch (error) {
      console.error('Error fetching devices:', error)
      toast.error('Error fetching devices')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAlerts = async () => {
    try {
      const response = await fetch('/api/v1/devices/alerts/')
      if (response.ok) {
        const data = await response.json()
        const alertList = Array.isArray(data) ? data : (data.results || [])
        setAlerts(Array.isArray(alertList) ? alertList : [])
      }
    } catch (error) {
      console.error('Error fetching alerts:', error)
    }
  }

  useEffect(() => {
    fetchDevices()
    fetchAlerts()

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDevices()
      fetchAlerts()
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  const acknowledgeAlert = async (alertId: string) => {
    try {
      const response = await fetch(`/api/v1/devices/alerts/${alertId}/acknowledge/`, {
        method: 'POST'
      })
      if (response.ok) {
        toast.success('Alert acknowledged')
        fetchAlerts()
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error)
      toast.error('Error acknowledging alert')
    }
  }

  const restartDevice = async (deviceId: string) => {
    try {
      const response = await fetch(`/api/v1/devices/${deviceId}/restart/`, {
        method: 'POST'
      })
      if (response.ok) {
        toast.success('Device restart initiated')
        fetchDevices()
      } else {
        toast.error('Failed to restart device')
      }
    } catch (error) {
      console.error('Error restarting device:', error)
      toast.error('Error restarting device')
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      online: 'bg-green-100 text-green-800',
      offline: 'bg-red-100 text-red-800',
      maintenance: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-200 text-red-900',
      warning: 'bg-orange-100 text-orange-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      online: '🟢',
      offline: '🔴',
      maintenance: '🟡',
      error: '❌',
      warning: '⚠️'
    }
    return icons[status] || '⚫'
  }

  const getAlertColor = (severity: string) => {
    const colors: Record<string, string> = {
      low: 'bg-blue-50 border-blue-200',
      medium: 'bg-yellow-50 border-yellow-200',
      high: 'bg-orange-50 border-orange-200',
      critical: 'bg-red-50 border-red-200'
    }
    return colors[severity] || 'bg-gray-50 border-gray-200'
  }

  const getUptimeColor = (uptime: number) => {
    if (uptime >= 95) return 'text-green-600'
    if (uptime >= 90) return 'text-yellow-600'
    return 'text-red-600'
  }

  const filteredDevices = devices.filter(device => {
    const matchesSearch =
      device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.device_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.location.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || device.status === statusFilter
    const matchesType = typeFilter === 'all' || device.type === typeFilter
    const matchesLocation = locationFilter === 'all' || device.location === locationFilter

    return matchesSearch && matchesStatus && matchesType && matchesLocation
  })

  const deviceStats = {
    total: devices.length,
    online: devices.filter(d => d.status === 'online').length,
    offline: devices.filter(d => d.status === 'offline').length,
    maintenance: devices.filter(d => d.status === 'maintenance').length,
    error: devices.filter(d => d.status === 'error' || d.status === 'warning').length
  }

  const locations = [...new Set(devices.map(d => d.location))]
  const deviceTypes = [...new Set(devices.map(d => d.type))]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Device Status</h1>
          <p className="text-gray-600">Monitor and manage medical devices and equipment</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              fetchDevices()
              fetchAlerts()
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Refresh
          </button>
          <button className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">
            Add Device
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {[
          { label: 'Total Devices', value: deviceStats.total, color: 'blue', icon: '📱' },
          { label: 'Online', value: deviceStats.online, color: 'green', icon: '🟢' },
          { label: 'Offline', value: deviceStats.offline, color: 'red', icon: '🔴' },
          { label: 'Maintenance', value: deviceStats.maintenance, color: 'yellow', icon: '🟡' },
          { label: 'Issues', value: deviceStats.error, color: 'orange', icon: '⚠️' }
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-6 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className="text-2xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Active Alerts */}
      {alerts.filter(alert => !alert.acknowledged).length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Device Alerts</h3>
          <div className="space-y-3">
            {alerts.filter(alert => !alert.acknowledged).slice(0, 5).map((alert) => (
              <div key={alert.id} className={`p-4 rounded-lg border ${getAlertColor(alert.severity)}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                        alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                        alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{alert.device_name}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => acknowledgeAlert(alert.id)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Acknowledge
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="maintenance">Maintenance</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              {deviceTypes.map(type => (
                <option key={type} value={type} className="capitalize">{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Locations</option>
              {locations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Devices Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading devices...</p>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg border">
            <p className="text-gray-500">No devices found</p>
          </div>
        ) : (
          filteredDevices.map((device) => (
            <div key={device.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
              {/* Device Header */}
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{device.name}</h3>
                  <p className="text-sm text-gray-600">{device.device_id}</p>
                  <p className="text-xs text-gray-500 capitalize">{device.type} • {device.location}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{getStatusIcon(device.status)}</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(device.status)}`}>
                    {device.status.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Device Info */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Uptime:</span>
                  <span className={`font-medium ${getUptimeColor(device.uptime_percentage)}`}>
                    {device.uptime_percentage}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Load:</span>
                  <span className="font-medium text-gray-900">{device.current_load}%</span>
                </div>
                {device.temperature && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Temperature:</span>
                    <span className="font-medium text-gray-900">{device.temperature}°C</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Last Seen:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(device.last_seen).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Load Bar */}
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      device.current_load > 80 ? 'bg-red-500' :
                      device.current_load > 60 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${device.current_load}%` }}
                  ></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => {
                    setSelectedDevice(device)
                    setShowDeviceModal(true)
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Details
                </button>
                <div className="flex space-x-2">
                  {device.status === 'error' && (
                    <button
                      onClick={() => restartDevice(device.id)}
                      className="text-green-600 hover:text-green-800 text-sm"
                    >
                      Restart
                    </button>
                  )}
                  <button className="text-gray-600 hover:text-gray-800 text-sm">
                    Configure
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Device Detail Modal */}
      {showDeviceModal && selectedDevice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Device Details - {selectedDevice.name}
              </h3>
              <button
                onClick={() => setShowDeviceModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Device ID:</span> {selectedDevice.device_id}</p>
                    <p><span className="font-medium">Type:</span> {selectedDevice.type}</p>
                    <p><span className="font-medium">Manufacturer:</span> {selectedDevice.manufacturer}</p>
                    <p><span className="font-medium">Model:</span> {selectedDevice.model}</p>
                    <p><span className="font-medium">Serial Number:</span> {selectedDevice.serial_number}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Location & Department</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Location:</span> {selectedDevice.location}</p>
                    <p><span className="font-medium">Department:</span> {selectedDevice.department}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Technical Details</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">IP Address:</span> {selectedDevice.ip_address}</p>
                    <p><span className="font-medium">Firmware:</span> {selectedDevice.firmware_version}</p>
                    <p><span className="font-medium">Uptime:</span> {selectedDevice.uptime_percentage}%</p>
                    <p><span className="font-medium">Current Load:</span> {selectedDevice.current_load}%</p>
                    {selectedDevice.temperature && (
                      <p><span className="font-medium">Temperature:</span> {selectedDevice.temperature}°C</p>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Maintenance Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Installed:</span> {new Date(selectedDevice.installed_date).toLocaleDateString()}</p>
                    <p><span className="font-medium">Warranty Expires:</span> {new Date(selectedDevice.warranty_expiry).toLocaleDateString()}</p>
                    {selectedDevice.maintenance_due && (
                      <p><span className="font-medium">Maintenance Due:</span> {new Date(selectedDevice.maintenance_due).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowDeviceModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700">
                Configure Device
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DeviceStatus